import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ResourceGrantSubjectType, User } from '@reflex/shared';
import type { OwnerSelectTeamOption } from '@reflex/ui/components/ownership/OwnerSelect';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { useUsers } from '@/hooks/auth/useUsers';
import { useMyTeams } from '@/hooks/auth/useMyTeams';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** The identity fields a grant carries about who it addresses. */
export interface ShareSubjectRef {
  subjectType: ResourceGrantSubjectType;
  subjectId: string;
}

export interface ShareSubjects {
  /** Every org member the caller can see — share candidates and label lookups. */
  users: User[];
  /** Teams the caller belongs to; the ones they may share with. */
  myTeams: OwnerSelectTeamOption[];
  /** Display name of the active org, for the general-access row. */
  orgName: string;
  /** Human name for a grant's subject, falling back to the raw id. */
  subjectLabel: (subject: ShareSubjectRef) => string;
}

/**
 * Resolves the human names behind a grant's `subjectType`/`subjectId` —
 * the one place the sharing surfaces (the share dialog and the Sharing
 * page) agree on what "Backend team" or "Alice Engineer" means.
 *
 * Team names come from the caller's own memberships, widened with the
 * org-wide team list when they may read it: an admin, or anyone looking at
 * a session shared with a team they are not on, would otherwise see a raw
 * `tem_…` id.
 */
export function useShareSubjects(): ShareSubjects {
  const { activeOrganizationId, activeMembership, can } = useAuth();
  const orgKey = useOrgKey();
  const usersQuery = useUsers();
  const myTeams = useMyTeams();
  const orgTeamsQuery = useQuery({
    queryKey: queryKeys.orgTeams(orgKey),
    queryFn: () => api.listTeams(activeOrganizationId as string),
    enabled: activeOrganizationId !== null && can('teams:read'),
  });

  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const orgTeams = orgTeamsQuery.data;
  const memberTeams = activeMembership?.teams;
  const orgName = activeMembership?.organization.name ?? 'this organization';

  const teamNameById = useMemo(() => {
    const byId = new Map<string, string>();
    // `memberTeams` (from `/auth/me`) and `orgTeams` (the live `listTeams`
    // query) are typed as `Array<…> | undefined`, but the production Sentry
    // event AGENT-FLOW-WEB-4X caught a non-iterable value reaching the
    // `orgTeams ?? []` loop here. `?? []` only guards null/undefined, so a
    // truthy non-array that drifts in through the OpenAPI-cast
    // (`generatedResult` is an untyped `Promise<T>`) or a poisoned React
    // Query cache slot throws `(c ?? []) is not iterable` and crashes the
    // entire Sharing page before any share row can render. Treat any value
    // that is not an actual array as empty, so a broken team lookup degrades
    // `subjectLabel` to the raw subject id instead of taking the whole page
    // down — and apply the same guard to `memberTeams` for parity, since it
    // relies on the same `/auth/me`-derived contract.
    const memberTeamsList = Array.isArray(memberTeams) ? memberTeams : [];
    const orgTeamsList = Array.isArray(orgTeams) ? orgTeams : [];
    for (const team of memberTeamsList) byId.set(team.id, team.name);
    for (const team of orgTeamsList) byId.set(team.id, team.name);
    return byId;
  }, [memberTeams, orgTeams]);

  const subjectLabel = useMemo(
    () =>
      (subject: ShareSubjectRef): string => {
        if (subject.subjectType === 'org') return `Everyone in ${orgName}`;
        if (subject.subjectType === 'team') {
          const name = teamNameById.get(subject.subjectId);
          return name ? `${name} team` : subject.subjectId;
        }
        const user = users.find((u) => u.id === subject.subjectId);
        return user ? (user.name ?? user.login) : subject.subjectId;
      },
    [orgName, teamNameById, users],
  );

  return { users, myTeams, orgName, subjectLabel };
}
